<template>
    <div class="rate-stars">
        <template v-for="score in 5">
            <rate-star
                :key="score"
                :score="score"
                :rated-score="ratedScore"
                :hover-score="hoverScore"
                @mouseover.native="hoverScore = score"
                @mouseleave.native="hoverScore = null"
                @pressed="rate(score)"
            />
        </template>

        <first-rating-modal
            :action="route('front.api.products.ratings.store', product)"
            :rating-type="ratingType"
            :score="selectedScore"
            :user-diet="userDiet"
            :callback="onFirstRatingSuccess"
        />
    </div>
</template>

<script>
    import RateStar from './RateStar';
    import FirstRatingModal from './FirstRatingModal';

    export default {
        components: {
            RateStar,
            FirstRatingModal,
        },
        props: {
            product: {
                type: Object,
                required: true,
            },
            ratingType: {
                type: String,
                required: true,
            },
            existingRating: {
                type: Object,
                default: null,
            },
            userHasRated: {
                type: Boolean,
                required: true,
            },
            userDiet: {
                type: String,
                default: null,
            },
        },

        data() {
            return {
                hoverScore: null,
                selectedScore: null,
            };
        },

        computed: {
            ratedScore() {
                if (! this.existingRating) {
                    return null;
                }

                return this.existingRating[this.ratingType + '_score'];
            },
        },

        methods: {
            rate(score) {
                if (this.isProductRatedInLocalStorage() && ! this.existingRating) {
                    let errorMessage;
                    if (this.isUserLoggedIn()) {
                        errorMessage = 'Det verkar som att du redan har satt betyg på den här '
                            + 'produkten i utloggat läge och kan därför inte ändra ditt betyg nu.';
                    } else {
                        errorMessage = 'Det verkar som att du redan har satt betyg på den här '
                            + 'produkten. Skapa ett konto och logga in för smidigare '
                            + 'betygsättning.';
                    }

                    this.$emit('rating-error', errorMessage);

                    return;
                }

                this.selectedScore = score;

                if (this.shouldShowFirstRatingModal()) {
                    this.$modal.show('first-rating-' + this.ratingType);

                    return;
                }

                this.storeRatingScore();
            },

            shouldShowFirstRatingModal() {
                return ! (this.isUserLoggedIn() && (this.userHasRated || this.userDiet))
                    && ! this.hasRatedAccordingToLocalStorage();
            },

            hasRatedAccordingToLocalStorage() {
                try {
                    return localStorage.hasRated || false;
                } catch (e) {
                    console.error(e);
                    return false;
                }
            },

            isProductRatedInLocalStorage() {
                return this.getLocalStorageRatedProductIds().indexOf(this.product.id) !== -1;
            },

            getLocalStorageRatedProductIds() {
                try {
                    return JSON.parse(localStorage.getItem('ratedProductIds')) || [];
                } catch (e) {
                    console.error(e);
                    return [];
                }
            },

            addProductToLocalStorageRatedProducts() {
                if (this.isProductRatedInLocalStorage()) {
                    return;
                }

                const ratedProductIds = this.getLocalStorageRatedProductIds();
                ratedProductIds.push(this.product.id);

                try {
                    localStorage.ratedProductIds = JSON.stringify(ratedProductIds);
                } catch (e) {
                    console.error(e);
                }
            },

            isUserLoggedIn() {
                return !! window.App.userId;
            },

            onFirstRatingSuccess(response) {
                this.$modal.hide('first-rating-' + this.ratingType);
                this.onSuccessfulRating(response.data.product, response.data.rating);
            },

            async storeRatingScore() {
                try {
                    const response = await axios.post(
                        route('front.api.products.ratings.store', this.product),
                        {
                            rating_type: this.ratingType,
                            score: this.selectedScore,
                            diet: this.getLocalStorageDiet(),
                        },
                    );
                    this.onSuccessfulRating(response.data.product, response.data.rating);
                } catch (error) {
                    if (error.response && error.response.data.message) {
                        flash_error(error.response.data.message);
                        return;
                    }

                    throw error;
                }
            },

            getLocalStorageDiet() {
                try {
                    return localStorage.diet || null;
                } catch (e) {
                    console.error(e);
                    return null;
                }
            },

            onSuccessfulRating(product, rating) {
                this.$emit('product-and-rating-updated', product, rating);
                flash_success('Tack för ditt betyg!');

                try {
                    localStorage.hasRated = true;
                    this.addProductToLocalStorageRatedProducts();
                    if (rating.diet) {
                        localStorage.diet = rating.diet;
                    } else {
                        localStorage.removeItem('diet');
                    }
                } catch (e) {
                    console.error(e);
                }
            },
        },
    };
</script>
